<template>
  <v-sheet class="d-flex flex-column" style="position:fixed;inset:0;z-index:100;">

    <!-- Header -->
    <scanner-header>
      <v-icon v-if="nfc_active" small color="success" class="mr-2">mdi-nfc</v-icon>
      <v-chip v-if="order_id" x-small outlined class="mr-2">#{{ order_id }}</v-chip>
      <v-btn icon small @click="confirmReset" v-if="order_id">
        <v-icon small>mdi-restart</v-icon>
      </v-btn>
    </scanner-header>

    <!-- Step indicator -->
    <v-stepper
      v-if="order_id"
      :value="step + 1"
      flat
      alt-labels
      class="flex-grow-0 elevation-0"
      style="background:transparent;"
    >
      <v-stepper-header>
        <template v-for="(s, i) in steps">
          <v-stepper-step
            :key="'step-' + i"
            :step="i + 1"
            :complete="step > i"
            :editable="step > i"
            @click="goToStep(i)"
          >
            {{ s.label }}
          </v-stepper-step>
          <v-divider v-if="i < steps.length - 1" :key="'div-' + i" />
        </template>
      </v-stepper-header>
    </v-stepper>

    <!-- Persistent total bar -->
    <v-toolbar v-if="order_id && step < 4" flat dense class="flex-grow-0">
      <span class="text-overline">{{ $t('pos.cart.total') || 'Gesamt' }}</span>
      <v-spacer />
      <span class="text-h6 font-weight-bold">{{ formatPrice(order.data.total || order_total) }} {{ order_currency }}</span>
    </v-toolbar>

    <!-- Setup: Select shop & price list -->
    <v-container v-if="!order_id" class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="6" md="4" class="text-center">
          <v-icon size="48" color="grey" class="mb-4">mdi-store</v-icon>
          <div class="text-h6 mb-4">{{ $t('pos.setup.title') || 'Kasse einrichten' }}</div>

          <v-select
            v-model="selected_shop"
            :items="available_shops"
            item-text="name"
            item-value="slug"
            return-object
            outlined dense
            label="Shop"
            class="mb-3"
            hide-details
          />

          <v-select
            v-if="selected_shop"
            v-model="selected_price_list"
            :items="selected_shop.price_lists"
            item-text="name"
            item-value="slug"
            return-object
            outlined dense
            label="Preisliste"
            class="mb-3"
            hide-details
          />

          <v-btn
            color="primary" depressed block large
            :loading="is_creating_order"
            :disabled="!selected_shop || !selected_price_list"
            @click="createOrder"
          >
            {{ $t('pos.setup.start') || 'Kasse starten' }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>

    <!-- Step 0: Scan & Cart -->
    <div v-else-if="step === 0" class="d-flex flex-column flex-grow-1 overflow-hidden">

      <!-- Scan input -->
      <div class="d-flex align-center pa-3 flex-shrink-0">
        <v-text-field
          ref="scanInput"
          v-model="scan_buffer"
          outlined dense
          hide-details
          :placeholder="$t('scanner.label.scan-or-enter-barcode') || 'Barcode scannen oder eingeben...'"
          inputmode="none"
          autocomplete="off"
          prepend-inner-icon="mdi-barcode-scan"
          @keydown.enter.prevent="onScan"
          @keydown.tab.prevent="onScan"
          @blur="refocusScan"
        />
        <v-btn icon class="ml-2" @click="onScan" :loading="is_scanning">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
      </div>

      <!-- Cart items -->
      <v-list v-if="cart_items.length" class="flex-grow-1 overflow-y-auto pa-0">
        <v-list-item v-for="item in cart_items" :key="item.id" class="px-3">
          <v-list-item-avatar tile size="40" class="rounded">
            <v-img v-if="item.related_object && item.related_object.thumbnail" :src="item.related_object.thumbnail" />
            <v-icon v-else small>mdi-package-variant</v-icon>
          </v-list-item-avatar>

          <v-list-item-content>
            <v-list-item-title class="text-body-2">{{ item.name }}</v-list-item-title>
            <v-list-item-subtitle class="text-caption font-weight-medium">{{ item.sku }}</v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action class="flex-row align-center my-0" style="gap:4px;">
            <v-btn icon x-small @click="updateQty(item, -1)">
              <v-icon x-small>mdi-minus</v-icon>
            </v-btn>
            <span class="text-body-2 font-weight-bold" style="min-width:24px;text-align:center;">{{ item.quantity }}</span>
            <v-btn icon x-small @click="updateQty(item, 1)">
              <v-icon x-small>mdi-plus</v-icon>
            </v-btn>
          </v-list-item-action>

          <v-list-item-action class="my-0 ml-2">
            <span class="text-body-2 font-weight-bold">{{ formatPrice(item.total_including_tax) }}</span>
          </v-list-item-action>

          <v-list-item-action class="my-0 ml-1">
            <v-btn icon x-small @click="removeItem(item)">
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>

      <div v-else class="d-flex flex-column align-center justify-center flex-grow-1" style="opacity:0.6;">
        <v-icon size="40" color="grey">mdi-cart-outline</v-icon>
        <div class="mt-2 text-body-2">{{ $t('pos.cart.empty') || 'Warenkorb ist leer' }}</div>
      </div>

      <!-- Cart footer -->
      <v-footer v-if="cart_items.length" color="transparent" class="pa-3 flex-shrink-0">
        <v-btn color="primary" depressed block large @click="step = 1" :loading="order.is_loading">
          {{ $t('pos.cart.continue') || 'Weiter' }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-footer>
    </div>

    <!-- Step 1: Shipping -->
    <div v-else-if="step === 1" class="d-flex flex-column flex-grow-1 overflow-hidden">
      <div class="flex-grow-1 overflow-y-auto pa-4">
        <div class="text-subtitle-1 font-weight-bold d-flex align-center mb-4">
          <v-icon class="mr-2">mdi-truck</v-icon>
          {{ $t('pos.shipping.title') || 'Versand' }}
        </div>

        <v-progress-linear v-if="order.shipping_options === null" indeterminate color="primary" class="mb-4" />

        <v-item-group v-if="order.shipping_options" v-model="selected_shipping_id">
          <v-card
            v-for="opt in order.shipping_options"
            :key="opt.id"
            outlined
            class="mb-2 pa-3 d-flex align-center"
            :class="{ 'primary--border': selected_shipping_id === opt.id }"
            :style="selected_shipping_id === opt.id ? 'border-color: var(--v-primary-base) !important;' : ''"
            @click="selectShipping(opt)"
          >
            <span class="font-weight-medium flex-grow-1">{{ opt.name }}</span>
            <span class="text-body-2 ml-2" style="opacity:0.6;">{{ formatPrice(opt.price_including_tax) }} {{ order_currency }}</span>
            <v-icon v-if="selected_shipping_id === opt.id" small color="primary" class="ml-2">mdi-check-circle</v-icon>
          </v-card>
        </v-item-group>
      </div>

      <v-footer color="transparent" class="pa-3 d-flex justify-space-between flex-shrink-0">
        <v-btn text @click="step = 0">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('pos.back') || 'Zurück' }}
        </v-btn>
        <v-btn color="primary" depressed @click="step = 2" :disabled="!selected_shipping_id">
          {{ $t('pos.continue') || 'Weiter' }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-footer>
    </div>

    <!-- Step 2: Payment -->
    <div v-else-if="step === 2" class="d-flex flex-column flex-grow-1 overflow-hidden">
      <div class="flex-grow-1 overflow-y-auto pa-4">
        <div class="text-subtitle-1 font-weight-bold d-flex align-center mb-4">
          <v-icon class="mr-2">mdi-credit-card-outline</v-icon>
          {{ $t('pos.payment.title') || 'Zahlung' }}
        </div>

        <v-progress-linear v-if="order.payment_options === null" indeterminate color="primary" class="mb-4" />

        <v-item-group v-if="order.payment_options">
          <v-card
            v-for="opt in order.payment_options"
            :key="opt.id"
            outlined
            class="mb-2 pa-3 d-flex align-center"
            :style="selected_payment_id === opt.id ? 'border-color: var(--v-primary-base) !important;' : ''"
            @click="selectPayment(opt)"
          >
            <span class="font-weight-medium flex-grow-1">{{ opt.name }}</span>
            <span v-if="opt.fee_including_tax" class="text-body-2 ml-2" style="opacity:0.6;">
              +{{ formatPrice(opt.fee_including_tax) }} {{ order_currency }}
            </span>
            <v-icon v-if="selected_payment_id === opt.id" small color="primary" class="ml-2">mdi-check-circle</v-icon>
          </v-card>
        </v-item-group>
      </div>

      <v-footer color="transparent" class="pa-3 d-flex justify-space-between flex-shrink-0">
        <v-btn text @click="step = 1">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('pos.back') || 'Zurück' }}
        </v-btn>
        <v-btn color="primary" depressed @click="step = 3" :disabled="!selected_payment_id">
          {{ $t('pos.continue') || 'Weiter' }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>
      </v-footer>
    </div>

    <!-- Step 3: Confirmation -->
    <div v-else-if="step === 3" class="d-flex flex-column flex-grow-1 overflow-hidden">
      <div class="flex-grow-1 overflow-y-auto pa-4">
        <div class="text-subtitle-1 font-weight-bold d-flex align-center mb-4">
          <v-icon class="mr-2">mdi-clipboard-check-outline</v-icon>
          {{ $t('pos.confirm.title') || 'Zusammenfassung' }}
        </div>

        <!-- Order summary -->
        <v-card outlined class="mb-3 pa-3">
          <div class="text-overline mb-2">{{ $t('pos.confirm.items') || 'Artikel' }}</div>
          <div v-for="item in cart_items" :key="item.id" class="d-flex justify-space-between text-body-2 py-1">
            <span>{{ item.quantity }}x {{ item.name }}</span>
            <span>{{ formatPrice(item.total_including_tax) }}</span>
          </div>
        </v-card>

        <v-card v-if="order.data.shipping_method_name" outlined class="mb-3 pa-3">
          <div class="text-overline mb-2">{{ $t('pos.confirm.shipping') || 'Versand' }}</div>
          <div class="d-flex justify-space-between text-body-2">
            <span>{{ order.data.shipping_method_name }}</span>
            <span>{{ formatPrice(order.data.shipping_total_including_tax || 0) }}</span>
          </div>
        </v-card>

        <v-card v-if="order.data.payment_method_name" outlined class="mb-3 pa-3">
          <div class="text-overline mb-2">{{ $t('pos.confirm.payment') || 'Zahlung' }}</div>
          <div class="text-body-2">{{ order.data.payment_method_name }}</div>
        </v-card>

        <v-card outlined class="mb-3 pa-3" style="border-color: var(--v-primary-base) !important;">
          <div class="d-flex justify-space-between text-h6 font-weight-bold">
            <span>{{ $t('pos.confirm.total') || 'Gesamtbetrag' }}</span>
            <span>{{ formatPrice(order.data.total || order_total) }} {{ order_currency }}</span>
          </div>
        </v-card>

        <!-- Customer identification -->
        <v-card v-if="customer_uid" outlined class="pa-3 d-flex align-center" style="border-color:#4caf50 !important;">
          <v-icon small class="mr-2">mdi-account-check</v-icon>
          <span class="text-body-2 font-weight-medium flex-grow-1" style="word-break:break-all;font-family:monospace;">{{ customer_uid }}</span>
          <v-btn icon x-small @click="customer_uid = null" class="ml-2">
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </v-card>

        <v-card v-else outlined class="pa-6 text-center" style="border-style:dashed;cursor:pointer;" @click="focusLoyaltyInput">
          <v-icon size="28" color="grey">mdi-card-account-details-outline</v-icon>
          <div class="text-body-2 font-weight-medium mt-2" style="opacity:0.7;">Kundenkarte scannen...</div>
          <div class="text-caption mt-1" style="opacity:0.35;">NFC, Barcode oder QR-Code</div>
          <input
            ref="loyaltyInput"
            style="position:absolute;opacity:0;width:0;height:0;pointer-events:none;"
            type="text"
            v-model="loyalty_buffer"
            @keydown.enter.prevent="onLoyaltyScan"
            @keydown.tab.prevent="onLoyaltyScan"
            autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
          />
        </v-card>
      </div>

      <v-footer color="transparent" class="pa-3 d-flex justify-space-between flex-shrink-0">
        <v-btn text @click="step = 2">
          <v-icon left>mdi-arrow-left</v-icon>
          {{ $t('pos.back') || 'Zurück' }}
        </v-btn>
        <v-btn color="success" depressed large @click="placeOrder" :loading="is_placing_order">
          <v-icon left>mdi-check</v-icon>
          {{ $t('pos.confirm.place') || 'Bestellung aufgeben' }}
        </v-btn>
      </v-footer>
    </div>

    <!-- Step 4: Done -->
    <div v-else-if="step === 4" class="d-flex flex-column align-center justify-center flex-grow-1">
      <v-icon size="64" color="success" class="mb-4">mdi-check-circle-outline</v-icon>
      <div class="text-h5 font-weight-bold">{{ $t('pos.done.title') || 'Bestellung erstellt!' }}</div>
      <div class="text-body-1 mt-1" style="opacity:0.5;font-family:monospace;" v-if="order_id">#{{ order_id }}</div>
      <v-btn color="primary" depressed large class="mt-6" @click="startNewOrder">
        <v-icon left>mdi-plus</v-icon>
        {{ $t('pos.done.new_order') || 'Neue Bestellung' }}
      </v-btn>
    </div>

    <!-- Toast -->
    <v-snackbar v-model="toast.show" :color="toast.color" :timeout="2000" top>
      <div class="d-flex align-center">
        <img v-if="toast.thumbnail" :src="toast.thumbnail" class="mr-3" style="width:36px;height:36px;border-radius:6px;object-fit:cover;" />
        <v-icon v-else left dark small>{{ toast.icon }}</v-icon>
        {{ toast.text }}
      </div>
    </v-snackbar>

  </v-sheet>
</template>

<script>
import { sessionStore } from '@/store/pinia/sessionStore'
import { orderStore } from '@/store/pinia/orderStore'
import EventService from '@/services/APIService'
import ScannerHeader from '@/components/_scanner/ScannerHeader.vue'

export default {
  name: 'ScannerPOC',

  components: { ScannerHeader },

  setup() {
    const session = sessionStore()
    const order = orderStore()
    return { session, order }
  },

  data() {
    return {
      step: 0,
      steps: [
        { label: 'Scan' },
        { label: 'Versand' },
        { label: 'Zahlung' },
        { label: 'Fertig' },
      ],

      // Setup
      selected_shop: null,
      selected_price_list: null,
      is_creating_order: false,

      // Order
      order_id: null,

      // Selections
      selected_shipping_id: null,
      selected_payment_id: null,

      // Scanning
      scan_buffer: '',
      is_scanning: false,

      // NFC
      nfc_supported: typeof window !== 'undefined' && 'NDEFReader' in window,
      nfc_active: false,
      nfc_reader: null,
      nfc_abort: null,

      // Customer identification (confirmation step)
      customer_uid: null,
      loyalty_buffer: '',

      // Toast
      toast: { show: false, text: '', color: 'success', icon: 'mdi-check', thumbnail: null },

      // Placing
      is_placing_order: false,
    }
  },

  computed: {
    available_shops() {
      if (!this.session.shops) return []
      return this.session.shops.filter(s => s.is_active && s.price_lists && s.price_lists.length > 0)
    },

    cart_items() {
      if (!this.order.data || !this.order.data.line_items) return []
      return this.order.data.line_items.filter(l => l.type === 'product')
    },

    order_total() {
      if (this.order.data && this.order.data.total != null) return this.order.data.total
      return this.cart_items.reduce((sum, item) => sum + Number(item.total_including_tax || 0), 0)
    },

    order_currency() {
      return this.order.data?.currency || 'EUR'
    },
  },

  methods: {

    formatPrice(val) {
      return Number(val || 0).toFixed(2)
    },

    showToast(text, type = 'success', thumbnail = null) {
      this.toast = {
        show: true,
        text,
        color: type === 'error' ? 'error' : 'success',
        icon: type === 'error' ? 'mdi-alert-circle' : 'mdi-check',
        thumbnail,
      }
    },

    // --- Setup ---

    async createOrder() {
      this.is_creating_order = true
      try {
        const res = await this.$http.put('/api/v3/order', {
          shop: this.selected_shop.slug,
          price_list: this.selected_price_list.slug,
        })
        this.order_id = res.data.payload.id
        this.order.$reset()
        this.order.order_id = this.order_id

        // Load order with line_items, totals, mods
        await this.order.loadOrder(['line_items', 'totals', 'shop', 'mods'])
        this.order.loadShippingOptions()
        this.order.loadPaymentOptions()

        this.$nextTick(() => {
          this.focusScan()
          this.startNfc()
        })
      } catch (e) {
        console.error('Failed to create order', e)
      } finally {
        this.is_creating_order = false
      }
    },

    // --- Scanning ---

    focusScan() {
      this.$refs.scanInput && this.$refs.scanInput.focus()
    },

    refocusScan(e) {
      const related = e && e.relatedTarget
      if (related && (related.tagName === 'BUTTON' || related.closest('button, .v-btn, input'))) return
      setTimeout(() => this.focusScan(), 50)
    },

    onScan() {
      const code = this.scan_buffer.trim()
      this.scan_buffer = ''
      if (code) this.handleScanCode(code)
    },

    async handleScanCode(code) {
      if (this.is_scanning) return
      this.is_scanning = true

      try {
        const res = await this.$http.post('/api/v3/scan', { code, expand: ['prices'] })
        const payload = res.data.payload

        if (!payload.found) {
          this.showToast(`Artikel "${code}" nicht gefunden`, 'error')
          return
        }

        await this.order.addItem({
          id: payload.product.id,
          line_type: 'product',
        })

        this.showToast(`${payload.product.title || payload.product.sku} hinzugefügt`, 'success', payload.product.thumbnail)

      } catch (e) {
        this.showToast(e?.response?.data?.error || 'Scan fehlgeschlagen', 'error')
      } finally {
        this.is_scanning = false
        this.focusScan()
      }
    },

    // --- NFC ---

    async startNfc() {
      if (!this.nfc_supported || this.nfc_active) return
      try {
        this.nfc_abort = new AbortController()
        const reader = new window.NDEFReader()
        this.nfc_reader = reader
        await reader.scan({ signal: this.nfc_abort.signal })
        this.nfc_active = true

        reader.addEventListener('reading', ({ message, serialNumber }) => {
          let value = serialNumber || ''
          for (const record of message.records) {
            if (record.recordType === 'text') {
              value = new TextDecoder(record.encoding || 'utf-8').decode(record.data)
              break
            }
            if (record.recordType === 'url') {
              value = new TextDecoder().decode(record.data)
              break
            }
          }
          if (!value) value = serialNumber
          if (!value) return

          if (this.step === 3) {
            this.customer_uid = serialNumber || value
            this.showToast('Kundenkarte: ' + (serialNumber || value), 'success')
          } else {
            this.handleScanCode(value)
          }
        })

        reader.addEventListener('readingerror', () => {
          this.showToast('NFC Lesefehler', 'error')
        })
      } catch (e) {
        this.nfc_active = false
        if (e.name === 'NotAllowedError') {
          this.showToast('NFC Berechtigung verweigert', 'error')
        }
      }
    },

    stopNfc() {
      if (this.nfc_abort) {
        this.nfc_abort.abort()
        this.nfc_abort = null
      }
      this.nfc_reader = null
      this.nfc_active = false
    },

    // --- Loyalty card (scanner wedge input) ---

    focusLoyaltyInput() {
      this.$refs.loyaltyInput && this.$refs.loyaltyInput.focus()
    },

    onLoyaltyScan() {
      const val = this.loyalty_buffer.trim()
      this.loyalty_buffer = ''
      if (val) {
        this.customer_uid = val
        this.showToast('Kundenkarte: ' + val, 'success')
      }
    },

    // --- Cart ---

    updateQty(item, delta) {
      const newQty = Number(item.quantity) + delta
      if (newQty <= 0) {
        this.removeItem(item)
        return
      }
      this.order.updateItem({ id: item.id, quantity: newQty })
    },

    removeItem(item) {
      this.order.removeItem(item.id)
    },

    // --- Shipping ---

    async selectShipping(opt) {
      this.selected_shipping_id = opt.id
      await this.order.updateOrder({ shipping_method_id: opt.id })
      await this.order.loadOrder(['line_items', 'totals', 'mods'])
      this.order.loadPaymentOptions()
    },

    // --- Payment ---

    async selectPayment(opt) {
      this.selected_payment_id = opt.id
      await this.order.updateOrder({ payment_method_id: opt.id })
      await this.order.loadOrder(['line_items', 'totals', 'mods'])
    },

    // --- Confirm ---

    async placeOrder() {
      this.is_placing_order = true
      try {
        await this.$http.post('/api/v3/order/' + this.order_id + '/place')
        this.step = 4
      } catch (e) {
        console.warn('Transition failed, continuing anyway (POC)', e)
        this.step = 4
      } finally {
        this.is_placing_order = false
      }
    },

    // --- Navigation ---

    goToStep(i) {
      if (i < this.step) this.step = i
    },

    confirmReset() {
      if (confirm('Aktuelle Bestellung verwerfen?')) {
        this.startNewOrder()
      }
    },

    startNewOrder() {
      this.order.$reset()
      this.order_id = null
      this.step = 0
      this.scan_buffer = ''
      this.selected_shipping_id = null
      this.selected_payment_id = null
      this.customer_uid = null
      this.loyalty_buffer = ''
      this.stopNfc()
    },
  },

  mounted() {
    this.session.loadShops()
    this.$watch('available_shops', (shops) => {
      if (shops.length === 1) {
        this.selected_shop = shops[0]
        if (shops[0].price_lists.length === 1) {
          this.selected_price_list = shops[0].price_lists[0]
        }
      }
    }, { immediate: true })
  },

  watch: {
    step(val) {
      if (val === 3) {
        this.$nextTick(() => this.focusLoyaltyInput())
        this.startNfc()
      }
      if (val === 0) {
        this.$nextTick(() => this.focusScan())
      }
    },
  },

  beforeDestroy() {
    this.stopNfc()
  },
}
</script>
